import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { fetchAuthSession } from '@aws-amplify/auth';
import { environment } from '@env/environment';
import { firstValueFrom } from 'rxjs';
import {
    Category,
    Client,
    ProductMask,
    ExternalApis
} from '@app/core/services/api/API.service';
import { DetailProduct } from '@app/shared/interfaces';
import { AuthService } from '@app/core/auth/auth.service';

export interface CategoryMaskItem extends Category {
    originalName: string;
    productsLength: number;
}

export interface ClientMaskItem extends Client {
    productsLength: number;
}

export interface ExternalApiItem extends ExternalApis {
    productsLength: number;
}

export interface ProductsBySearchTermResponse {
    products: DetailProduct[];
    productsL: number;
    categories: Category[];
}

@Injectable({
    providedIn: 'root'
})
export class SlsAdminSellerService {
    constructor(private http: HttpClient) {}
    private _authService = inject(AuthService);

    async listClientsMasks(): Promise<ClientMaskItem[]> {
        try {
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });

            const response = await firstValueFrom(
                this.http.get<any>(
                    `${environment.apiAdminSeller}list-clients-masks`,
                    {
                        observe: 'response',
                        headers
                    }
                )
            );
            if (response) {
                return response.body.data as ClientMaskItem[];
            }
        } catch (err) {
            console.error(err);
        }
    }

    async listCategoriesMasks(): Promise<CategoryMaskItem[]> {
        try {
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });

            const response = await firstValueFrom(
                this.http.get<any>(
                    `${environment.apiAdminSeller}list-categories-masks`,
                    {
                        observe: 'response',
                        headers
                    }
                )
            );
            if (response) {
                return response.body.data as CategoryMaskItem[];
            }
        } catch (err) {
            console.error(err);
        }
    }

    async listProductsMasks(idCategory: string = null): Promise<any> {
        try {
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });
            let url = `${environment.apiAdminSeller}list-products-masks`;
            if (idCategory) {
                url += `/${idCategory}`;
            }
            const response = await firstValueFrom(
                this.http.get<any>(url, {
                    observe: 'response',
                    headers
                })
            );
            if (response) {
                return response.body.data;
            }
        } catch (err) {
            console.error(err);
        }
    }

    async listClientProductsMasks(clientId: string): Promise<ProductMask[]> {
        try {
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });

            const response = await firstValueFrom(
                this.http.get<any>(
                    `${environment.apiAdminSeller}list-client-products-masks/${clientId}`,
                    {
                        observe: 'response',
                        headers
                    }
                )
            );
            if (response) {
                return response.body.data as ProductMask[];
            }
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    async listPanggaProducts(
        useAuth: boolean = true,
        limit: number = 10,
        showPublicPrices?: boolean
    ): Promise<ProductsBySearchTermResponse> {
        try {
            let headers: HttpHeaders;
            if (useAuth) {
                headers = await this._authService.generateHeaders();
            } else {
                headers = new HttpHeaders({
                    'Content-Type': 'application/json'
                });
            }

            const params: Record<string, string> = {};
            if (!useAuth) {
                const publicPrices =
                    showPublicPrices !== undefined ? showPublicPrices : true;
                params.showPublicPrices = publicPrices ? 'true' : 'false';
            }
            if (limit > 0) {
                params.limit = String(
                    Math.min(100, Math.max(1, Math.floor(limit)))
                );
            }

            const response = await firstValueFrom(
                this.http.get<ProductsBySearchTermResponse>(
                    `${environment.apiAdminSeller}list-pangga-products`,
                    {
                        observe: 'response',
                        headers,
                        params:
                            Object.keys(params).length > 0 ? params : undefined
                    }
                )
            );
            if (response) {
                return response.body as ProductsBySearchTermResponse;
            }
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    async listApis(): Promise<any> {
        try {
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });
            const response = await firstValueFrom(
                this.http.get<any>(`${environment.apiAdminSeller}list-apis`, {
                    headers
                })
            );
            if (response) {
                return response.data;
            }
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    async listProductsByApi(apiId: string): Promise<DetailProduct[]> {
        try {
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });
            const response = await firstValueFrom(
                this.http.get<any>(
                    `${environment.apiAdminSeller}list-products-by-api/${apiId}`,
                    { headers }
                )
            );
            if (response) {
                return response.data as DetailProduct[];
            }
        } catch (err) {
            console.log(err);
            return null;
        }
    }
}
